import { NotificationTwoTone } from '@ant-design/icons';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import {
  Divider,
  Form,
  Image,
  message,
  Modal,
  Select,
  Space,
  Typography,
} from 'antd';
import axios, { type AxiosResponse } from 'axios';
import dayjs from 'dayjs';
import moment, { type Moment } from 'moment';
import React, { useState } from 'react';
import useSWR from 'swr';

import deleteSvg from '@/assets/pink-one/delete.svg';
import editIconSvg from '@/assets/pink-one/edit.svg';
import { ImagePlaceholder } from '@/components/UI/iconComponents';
import LoadingSpin from '@/components/UI/LoadingSpin';

import sharedStyles from '../sharedStyles.module.scss';
import type {
  AddReservationFormValue,
  CleanReservationDetailsResponse,
  CleanReserveList,
  MemberInfoAndTicketList,
} from '../sharedTypes';
import {
  fetchReservationDatesStatus,
  getCleanKindInformation,
  getCleanKindOptions,
  getCommunityAndHouseholdNumberOptions,
  getMemberPhoneNumberOptions,
  getReservationDateStatus,
  getTicketId,
} from './helper';
import styles from './index.module.scss';
import OrderContent from './OrderContent';
import PaymentInfoModal from './PaymentInfoModal';
import type { ReservationDateStatus } from './types';
import UpdateReservationModal from './UpdateReservationModal';

type EditFormModalProps = {
  isOpenMainModal: boolean;
  hideAddFormModal: () => void;
  getLatestBookingList: () => void;
};

const { Item, useForm } = Form;
const { Text } = Typography;
const { confirm } = Modal;

const AddReservation: React.FC<EditFormModalProps> = ({
  isOpenMainModal,
  hideAddFormModal,
  getLatestBookingList,
}) => {
  const {
    data: memberListAndTicketList,
    isLoading: isMemberInfoFetcherLoading,
  } = useSWR(
    '/ezcon/api/fetchMemberListAndAvailableTickets?reservationType=一般',
    async (url) => {
      const res: AxiosResponse<MemberInfoAndTicketList[]> = await axios({
        method: 'GET',
        url,
        headers: { authorization: localStorage.getItem('feathers-jwt') },
      });
      return res.data;
    },
    { revalidateOnFocus: false }
  );

  const [mainForm] = useForm<
    AddReservationFormValue & { freeItem: number } & {
      [itemId: string]: number | null;
    }
  >();

  const userId = Form.useWatch('phoneNumber', mainForm);
  const cleanKindName = Form.useWatch('cleanKind', mainForm);

  const [cleanKindInformation, setCleanKindInformation] =
    useState<CleanReservationDetailsResponse>();
  const [showCleanContent, setShowCleanContent] = useState(false);
  const [cleanReserveList, setCleanReserveList] = useState<CleanReserveList>();
  const [editingReservation, setEditingReservation] = useState<
    CleanReserveList['clean'][0] & { order: number }
  >();
  const [isShowQuantityError, setIsShowQuantityError] = useState(false);
  const [isEditReservation, setIsEditReservation] = useState(false);
  const [reservationDatesStatus, setReservationDatesStatus] = useState<
    ReservationDateStatus['days']
  >([]);

  const memberPhoneNumberOptions = getMemberPhoneNumberOptions(
    memberListAndTicketList,
    userId?.key
  );

  const cleanKindOptions = getCleanKindOptions(
    memberListAndTicketList,
    userId?.key
  );

  const communityAndHouseholdNumberOptions =
    getCommunityAndHouseholdNumberOptions(memberListAndTicketList, userId?.key);

  const handleMainFormValuesChange = async (
    changedValues: AddReservationFormValue,
    allValues: AddReservationFormValue
  ) => {
    if (Object.prototype.hasOwnProperty.call(changedValues, 'phoneNumber')) {
      mainForm.setFieldsValue({
        cleanKind: undefined,
        communityAndHouseholdNumber: undefined,
      });
    }
    if (Object.prototype.hasOwnProperty.call(changedValues, 'cleanKind')) {
      mainForm.setFieldValue('communityAndHouseholdNumber', undefined);
    }
    if (
      Object.prototype.hasOwnProperty.call(
        changedValues,
        'communityAndHouseholdNumber'
      )
    ) {
      try {
        const data = await getCleanKindInformation(allValues.cleanKind);
        const ticketId = getTicketId(data.cleanServiceItems);
        if (ticketId) {
          const thisMonth = dayjs().valueOf();
          const communityAndHouseholdNumber =
            changedValues.communityAndHouseholdNumber.split('/');

          const reservationDatesInfo = await getReservationDateStatus({
            cleanKindId: data._id,
            month: thisMonth,
            communityName: communityAndHouseholdNumber[0],
            householdNumber: communityAndHouseholdNumber[1],
            userId: userId.key,
            ticketId,
            selectedDates: [],
          });

          setReservationDatesStatus(reservationDatesInfo.days);
        }
        setCleanKindInformation(data);
        setShowCleanContent(true);
      } catch (error) {
        message.error('資料載入失敗，請稍後再試');
      }
    }
  };

  const closeMainModal = () => {
    mainForm.resetFields();
    setIsShowQuantityError(false);
    setCleanReserveList(undefined);
    setShowCleanContent(false);
    getLatestBookingList();
    hideAddFormModal();
  };

  const deleteReservation = async (order: number) => {
    if (cleanReserveList && cleanKindInformation) {
      const newReservations = [...cleanReserveList.clean];
      newReservations.splice(order, 1);

      const selectedDates = newReservations.map((reservation) =>
        reservation.date.valueOf()
      );
      const newReservationDatesStatus = await fetchReservationDatesStatus({
        cleanKindInformation,
        cleanReserveList,
        userId: userId.key,
        selectedDates,
      });

      setReservationDatesStatus(newReservationDatesStatus);
      setCleanReserveList({
        ...cleanReserveList,
        clean: newReservations,
      });
    }
  };

  const showDeleteReservationAlert = (order: number) => {
    confirm({
      title: '確定刪除此筆資料？',
      icon: <ExclamationCircleOutlined />,
      content: '刪除後將無法復原',
      centered: true,
      onOk() {
        deleteReservation(order);
      },
    });
  };

  const updateReservationDatesStatus = async (selectedDate: Moment) => {
    const selectedDates = cleanReserveList?.clean.reduce(
      (dates: number[], reservation) => {
        if (reservation.date.isSame(selectedDate, 'date') === false) {
          dates.push(reservation.date.valueOf());
        }
        return dates;
      },
      []
    );
    if (selectedDates) {
      const newDatesStatus = await fetchReservationDatesStatus({
        cleanKindInformation,
        cleanReserveList,
        userId: userId.key,
        selectedDates,
      });

      setReservationDatesStatus(newDatesStatus);
    }
  };

  const userTicketAmount =
    memberListAndTicketList &&
    memberListAndTicketList[0].cleanKinds.find(
      (cleanKind) => cleanKind.cleanKindId === cleanKindName
    )?.amount;

  const updateReservationModalProps = {
    isEditReservation: {
      open: isEditReservation,
      setState: setIsEditReservation,
    },
    editingReservationData: {
      data: editingReservation,
      reset: () => setEditingReservation(undefined),
    },
    cleanReserveList: {
      data: cleanReserveList,
      setState: setCleanReserveList,
    },
    quantityError: {
      isShowQuantityError,
      setState: setIsShowQuantityError,
    },
    mainForm,
    userTicketAmount,
    reservationDatesStatusState: {
      data: reservationDatesStatus,
      setState: setReservationDatesStatus,
    },
    cleanKindInformation,
  };

  if (isMemberInfoFetcherLoading) {
    return <LoadingSpin />;
  }

  return (
    <Modal
      open={isOpenMainModal}
      title="新增預約"
      forceRender
      destroyOnClose
      className={styles.modal}
      footer={
        showCleanContent && (
          <>
            <UpdateReservationModal {...updateReservationModalProps} />
            <PaymentInfoModal
              cleanKinds={
                memberListAndTicketList && memberListAndTicketList[0].cleanKinds
              }
              cleanReserveList={cleanReserveList}
              cleanKindInfo={cleanKindInformation}
              closeMainModal={closeMainModal}
              validateMainFormValues={mainForm.validateFields}
            />
          </>
        )
      }
      onCancel={closeMainModal}
    >
      <Form
        form={mainForm}
        layout="vertical"
        preserve={false}
        onValuesChange={handleMainFormValuesChange}
      >
        {showCleanContent && (
          <>
            <Space className={styles.memberInfoContainer}>
              <div>
                <Text type="secondary">手機號碼</Text>
                <div>{mainForm.getFieldValue('phoneNumber')?.label}</div>
              </div>
              <div>
                <Text type="secondary" className={styles.householdTitle}>
                  清潔戶號
                </Text>
                <div className={styles.householdValue}>
                  {
                    mainForm
                      .getFieldValue('communityAndHouseholdNumber')
                      ?.split('/')[1]
                  }
                </div>
              </div>
            </Space>
            <Divider />
          </>
        )}
        <div className={showCleanContent ? styles.hidden : undefined}>
          <Item
            label={<Text type="secondary">會員</Text>}
            name="phoneNumber"
            rules={[{ required: true, message: '請選擇會員手機號碼' }]}
          >
            <Select
              allowClear
              showSearch
              showArrow={false}
              disabled={showCleanContent}
              placeholder="例如：(02)6633-6633"
              options={memberPhoneNumberOptions}
              optionFilterProp="label"
              labelInValue
            />
          </Item>
          <Item
            label={<Text type="secondary">清潔類別</Text>}
            name="cleanKind"
            rules={[{ required: true, message: '請選擇清潔類別' }]}
          >
            <Select
              disabled={showCleanContent}
              placeholder="請選擇清潔類別"
              options={cleanKindOptions}
            />
          </Item>
          <Item
            label={<Text type="secondary">清潔戶號</Text>}
            name="communityAndHouseholdNumber"
            rules={[{ required: true, message: '請選擇清潔戶號' }]}
          >
            <Select
              disabled={showCleanContent}
              placeholder="A-1-1"
              options={communityAndHouseholdNumberOptions}
            />
          </Item>
        </div>

        {showCleanContent && (
          <Item>
            {cleanKindInformation?.picture ? (
              <Image
                width="100%"
                height="100%"
                src={cleanKindInformation.picture}
              />
            ) : (
              <ImagePlaceholder />
            )}
            <hr />
            <h2>一般清潔</h2>
            <div className={styles.content}>
              <div>
                <p>
                  <NotificationTwoTone className={sharedStyles.noticeIcon} />
                  服務內容：
                </p>
              </div>
              <div className={styles.formItem}>
                <p className={styles.paragraph}>服務介紹:</p>
                <p>{cleanKindInformation?.description}</p>
              </div>
              <div className={styles.formItem}>
                <p className={styles.paragraph}>費用包含:</p>
                <p>{cleanKindInformation?.included}</p>
              </div>
            </div>
            <div className={styles.content}>
              <div>
                <p>
                  <NotificationTwoTone className={sharedStyles.noticeIcon} />
                  服務須知：
                </p>
              </div>
              <div className={styles.formItem}>
                <p className={styles.paragraph}>注意事項:</p>
                <p>
                  {cleanKindInformation?.warning
                    ? cleanKindInformation.warning
                    : '無'}
                </p>
              </div>
              <div className={styles.formItem}>
                <p className={styles.paragraph}>退費規則:</p>
                <p>{cleanKindInformation?.cleanReservationRule1}</p>
              </div>
            </div>
            {cleanReserveList && cleanReserveList.clean.length > 0 ? (
              <Item className={styles.marginTop}>
                <Space direction="vertical">
                  {cleanReserveList?.clean.map((item, index) => (
                    <Space key={index} align="center" size="large">
                      <div>
                        預約日期{index + 1}：{' '}
                        {moment(item.date).format('YYYY/MM/DD')}
                      </div>
                      <img
                        src={editIconSvg}
                        alt="editIcon"
                        className={styles.icon}
                        onClick={async () => {
                          await updateReservationDatesStatus(item.date);
                          setEditingReservation({ ...item, order: index });
                          setIsEditReservation(true);
                        }}
                      />
                      <img
                        alt="deleteIcon"
                        src={deleteSvg}
                        className={styles.icon}
                        onClick={() => showDeleteReservationAlert(index)}
                      />
                    </Space>
                  ))}
                </Space>
              </Item>
            ) : (
              <OrderContent
                reservationList={cleanReserveList}
                isShowQuantityError={isShowQuantityError}
                hideInputNumberErrorMsg={() => setIsShowQuantityError(false)}
                reservationDatesStatusState={{
                  data: reservationDatesStatus,
                  setState: setReservationDatesStatus,
                }}
                cleanKindInformation={cleanKindInformation}
              />
            )}
          </Item>
        )}
      </Form>
    </Modal>
  );
};

export default AddReservation;
